import { Consult, ConsultState } from 'app/models/domain/consult'
import { ConsultRepository } from 'app/api/consult/repository/ConsultRepository'
import { mintValidDownloadUrl } from 'app/api/consult/repository/StorageRepository'
import { AttachmentDto } from 'app/models/generated'
import { consultRepository } from 'app/di/RepositoryContainer'

export interface UpdateConsultUseCase {
    execute(
        consult: Consult,
        attachment: AttachmentDto,
        onComplete: (consultId: string) => void
    ): Promise<void>

    batchReSignAttachments(
        consult: Consult,
        attachments: AttachmentDto[]
    ): Promise<void>

    executeUpdateState(consult: Consult, state: ConsultState): Promise<void>
}

export class DefaultUpdateConsultUseCase implements UpdateConsultUseCase {
    private consultRepository: ConsultRepository

    constructor(consultRepository: ConsultRepository) {
        this.consultRepository = consultRepository
    }

    async execute(
        consult: Consult,
        attachment: AttachmentDto,
        onComplete: (consultId: string) => void
    ): Promise<void> {
        await this.batchReSignAttachments(consult, [attachment])
        onComplete(consult.id)
    }

    async batchReSignAttachments(
        consult: Consult,
        attachments: AttachmentDto[]
    ): Promise<void> {
        // Mint all URLs concurrently
        const reSignedEntries = await Promise.all(
            attachments.map(async (attachment) => {
                const contentUrl = await mintValidDownloadUrl(
                    attachment.contentStorageKey
                )
                const thumbnailUrl = attachment.thumbnailStorageKey
                    ? await mintValidDownloadUrl(attachment.thumbnailStorageKey)
                    : null

                return {
                    attachmentId: attachment.attachmentId,
                    content: contentUrl.toString(),
                    thumbnail: thumbnailUrl?.toString() ?? null,
                }
            })
        )

        // Build lookup map by attachmentId
        const urlMap = new Map(reSignedEntries.map((e) => [e.attachmentId, e]))

        // Replace in full array
        const allAttachments: AttachmentDto[] = (consult.attachments ?? []).map(
            (att) => {
                const fresh = urlMap.get(att.attachmentId)
                if (fresh) {
                    return new AttachmentDto({
                        attachmentId: att.attachmentId,
                        attachmentType: att.attachmentType,
                        content: fresh.content,
                        contentStorageKey: att.contentStorageKey,
                        thumbnail: fresh.thumbnail,
                        thumbnailStorageKey: att.thumbnailStorageKey,
                    })
                }
                return att
            }
        )

        await this.consultRepository.updatePresignedUrls(
            consult.id,
            allAttachments
        )
    }

    async executeUpdateState(
        consult: Consult,
        state: ConsultState
    ): Promise<void> {
        const dto = Consult.buildConsultDtoForState(consult, state)
        await consultRepository.updateForState(dto)
    }
}
